import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, Euro } from 'lucide-react';
import { AssistantState } from '../hooks/useAssistantState';

interface RecapitulatifStepProps {
  state: AssistantState;
  onComplete: () => void;
}

export const RecapitulatifStep = ({ state, onComplete }: RecapitulatifStepProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateTotal = () => {
    if (!state.selectedOffers) return 0;
    return state.selectedOffers.reduce((total, offer) => {
      return total + (offer.price * offer.quantity);
    }, 0);
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-primary/20">
        <CardHeader className="bg-primary/5">
          <CardTitle className="flex items-center gap-2 text-primary">
            Récapitulatif de votre demande
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Vérifiez attentivement toutes les informations avant de finaliser votre demande.
          </p>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {/* Section Type d'évènement */}
          <div className="space-y-3">
            <h4 className="font-semibold text-base border-b pb-1">Type d'évènement</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="default" className="text-sm">
                {state.userType === 'particulier' ? 'Particulier' : state.userType === 'collectivite' ? 'Collectivité' : 'Entreprise'}
              </Badge>
              {state.typeEvenement && (
                <Badge variant="outline" className="text-sm">
                  {state.typeEvenement}
                </Badge>
              )}
              {state.objectif && (
                <Badge variant="outline" className="text-sm">
                  {state.objectif}
                </Badge>
              )}
            </div>
          </div>

          {/* Section Date et Lieu */}
          <div className="space-y-3">
            <h4 className="font-semibold text-base border-b pb-1">Planning</h4>
            {state.dateEvenement && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="font-medium">Date principale :</span>
                </div>
                <p className="text-sm font-medium">
                  {formatDate(state.dateEvenement)}
                </p>
                {state.dateAlternative && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Date alternative : {formatDate(state.dateAlternative)}
                  </p>
                )}
              </div>
            )}
            
            {state.adresseLieu ? (
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="font-medium">Lieu :</span>
                </div>
                <p className="text-sm">
                  {state.adresseLieu.rue}<br/>
                  {state.adresseLieu.codePostal} {state.adresseLieu.ville}
                </p>
                <Badge variant="outline" className="mt-2 text-xs">
                  {state.adresseLieu.typeLieu}
                </Badge>
              </div>
            ) : (
              <div className="bg-muted/30 p-3 rounded-lg border border-dashed">
                <p className="text-sm text-muted-foreground">
                  Lieu à définir — nous vous accompagnons dans le choix du lieu idéal
                </p>
              </div>
            )}
          </div>

          {/* Section Participants */}
          <div className="space-y-3">
            <h4 className="font-semibold text-base border-b pb-1">Participants</h4>
            {(state.nombreInvites || state.nombreParticipants) && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="font-medium text-lg">
                    {state.nombreInvites || state.nombreParticipants}
                  </span>
                  <span className="text-sm">
                    {state.userType === 'particulier' ? 'invité(s)' : 'participant(s)'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Section Prestations */}
          {state.selectedOffers && state.selectedOffers.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-base border-b pb-1">Prestations envisagées</h4>
              <div className="space-y-2">
                {state.selectedOffers.map((offer) => (
                  <div key={offer.id} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                      <span className="text-sm font-medium">{offer.title}</span>
                      {offer.quantifiable && offer.quantity > 1 && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          ×{offer.quantity}
                        </Badge>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-sm">
                      Sur-mesure
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Budget et Contraintes */}
          {(state.budgetFourchette || state.budgetIndicatif || state.contraintesLogistiques) && (
            <div className="space-y-3">
              <h4 className="font-semibold text-base border-b pb-1">Informations complémentaires</h4>
              
              {(state.budgetFourchette || state.budgetIndicatif) && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-primary" />
                    <span className="font-medium">Budget envisagé :</span>
                    <span className="font-semibold">{state.budgetFourchette ?? `${state.budgetIndicatif}€`}</span>
                  </div>
                </div>
              )}

              {state.contraintesLogistiques && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="font-medium text-sm mb-2">Contraintes logistiques :</p>
                  <p className="text-sm text-muted-foreground">
                    {state.contraintesLogistiques}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Résumé final */}
          <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-primary">Votre projet en un coup d'œil</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Évènement <strong>{state.userType}</strong>
              {state.typeEvenement && <> - <strong>{state.typeEvenement}</strong></>}
              <br/>
              {(state.nombreInvites || state.nombreParticipants) && (
                <>Pour <strong>{state.nombreInvites || state.nombreParticipants}</strong> {state.userType === 'particulier' ? 'invité(s)' : 'participant(s)'}</>
              )}
              {state.dateEvenement && (
                <> le <strong>{formatDate(state.dateEvenement)}</strong></>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
        <p className="font-medium text-primary mb-1">Prochaine étape</p>
        <p className="text-sm text-muted-foreground">
          Dès vos coordonnées renseignées, un chef de projet dédié vous recontacte sous 24h pour affiner votre projet et vous adresser une proposition sur-mesure.
        </p>
      </div>

      <Button onClick={onComplete} className="w-full h-12 text-base font-medium">
        Valider et transmettre ma demande
      </Button>
    </div>
  );
};